import { Address } from './address';
import { DocumentRequirement } from './document-collection';
import { FleetBase } from './fleet';
import { PreliminaryInfo } from './person';

const SMALL_BUSINESS_REVENUE_THRESHOLD = 5_000_000_00;

export type Fleet = {
  companyEmail: string;
  shippingStreetAddress?: string;
  shippingStreetAddress2?: string;
  shippingCity?: string;
  shippingState?: string;
  shippingZip?: string;
} & Address &
  Omit<FleetBase, 'address' | 'shippingAddress'>;

export type Person = {
  id?: number;
  lastName: string;
  firstName: string;
  phone: string;
  dob: string;
  address: Address;
  ssn?: string;
  ssnLast4?: string;
  beneficialOwner: boolean;
  controlPerson: boolean;
  authorizedRep: boolean;
  isApplicant: boolean;
  personaInquiryId?: string;
};

export type QualifyingUserAndCompany = {
  people: Person[];
  fleet: Fleet;
  selectedOfferId?: string;
};

export const isSmallBusiness = ({ annualRevenue }: FleetBase): boolean =>
  (annualRevenue ?? 0) < SMALL_BUSINESS_REVENUE_THRESHOLD;

export type FinalApplicationStatus =
  | 'onboarded'
  | 'verified'
  | 'denied_business_type'
  | 'denied_duplicate'
  | 'denied_recent'
  | 'denied_consumer_credit_lock'
  | 'denied_revenue_too_low'
  | 'denied_too_young'
  | 'denied_consumer_credit_knockout'
  | 'denied_commercial_credit_knockout'
  | 'denied_commercial_thin_knockout'
  | 'denied_thin_files_knockout'
  | 'denied_thin_high_revenue_knockout'
  | 'denied_thin_low_revenue_knockout'
  | 'denied_unable_to_verify_id'
  | 'denied_state_credit_line_knockout'
  | 'denied_product_not_offered'
  | 'denied_manual_review'
  | 'denied'
  | 'denied_policy_no_compensating_factor'
  | 'denied_other'
  | 'denied_consumer_credit_knockout_manual'
  | 'denied_commercial_credit_knockout_manual'
  | 'denied_inactive_30_days'
  | 'denied_verification';

export const qualificationActions = [
  'collect_business_and_applicant_info',
  'select_offer',
  'get_ubos',
  'pre_collect_ubos',
  'verify_ubos',
  'counter_offer',
  'persona',
  'manual_review',
  'collect_documents',
  'collect_financials',
  'unlock_consumer_credit',
  'declined',
  'approved',
  'verified',
  'verify_revenue',
  'verify_fetching_revenue',
  'verification_review',
  'preview_card_order',
] as const;

export type CollectDocumentsNextAction = Extract<QualificationAction, 'collect_documents' | 'verify_revenue'>;

export type QualificationAction = (typeof qualificationActions)[number];

export type CreditTerms = {
  finalCreditLimit: number;
  billingCycle: number;
  gracePeriodDays: number;
};

export type Offer = {
  id: string;
  offerIndex: number;
  billingCycle: number;
  gracePeriodDays: number;
  userFee: number;
  fuelRebateHundredthsOfCent: number;
  nonFuelRebateBasisPoints: number;
  offerSummaryUrl?: string;
};

type ApplicantCredentials = {
  phoneNumber: string;
  email: string;
};

export type RevenueVerificationStatus = 'success' | 'error' | 'insufficient_data';

type ApplicationMeta = {
  revenueVerificationStatus?: RevenueVerificationStatus;
  conditionalApproval?: boolean;
};

export type QualificationStepResponse = {
  action: QualificationAction;
  finalStatus?: FinalApplicationStatus;
  application?: Partial<QualifyingUserAndCompany>;
  creditTerms?: CreditTerms;
  documentRequirements?: DocumentRequirement[];
  applicantCredentials?: ApplicantCredentials;
  applicationMeta?: ApplicationMeta;
  isSubaccount?: boolean;
  offers?: Offer[];
  acceptedOfferId?: string;
};

export type CollectBusinessApplicantInfoPage =
  | 'business_info_1'
  | 'business_info_2'
  | 'personal_info'
  | 'spend_intent'
  | 'business_verify';

type QualificationStepRequestMeta = {
  page: CollectBusinessApplicantInfoPage;
};

export type QualificationStepRequest = {
  action?: QualificationAction;
  application?: Partial<QualifyingUserAndCompany>;
  meta?: QualificationStepRequestMeta;
};

export type ApplicationRejectionType =
  | 'locked'
  | 'ineligible'
  | 'invalid'
  | 'unsupported_state'
  | 'inactive_application';

export type Promotion = {
  promoCode: string;
  description: string;
};

export type VerificationRequest = PreliminaryInfo & {
  promoCode?: string;
  partnerId?: string;
  referrerId?: string;
  embeddedSessionToken?: string;
};

export type OtpRequest = {
  phone: string;
  channel: 'sms' | 'call';
};

export type AnalyticsData = {
  userId: string;
  customerId: string;
  fleetUserExists: boolean;
};
